/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.14.0
 */

import { Fragment, useEffect } from 'react';

import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';

import { Button, LinkBlock } from '../../../../../components/common';
import { ImgStatusError } from '../../../../../components/common/Block/Img';
import { IOptionItemProps } from '../../../../../components/common/Select/Select';
import { FormCondition } from '../../../../../modules/form/components/FormCondition';
import FormFieldInputPhone from '../../../../../modules/form/components/FormFieldInputPhone';
import FormFieldSelect from '../../../../../modules/form/components/FormFieldSelect';
import { getErrorTranslation } from '../../../../../modules/internationalization/helpers/intGetErrorTrnaslation';
import { makeSelectTranslationCommon } from '../../../../../modules/internationalization/intSelectors';
import Modal from '../../../../../modules/modal/components/Modal';
import { ModalCenterStyled } from '../../../../../modules/modal/components/styled/ModalCenterStyled';
import { modalCloseAll, modalSetStatusAction } from '../../../../../modules/modal/modalActions';
import { EModalStatus, IModalRenderProps } from '../../../../../modules/modal/types/modalTypes';
import ButtonServiceUserDenied from '../../../../../modules/role/components/ButtonServiceUserDenied';
import declination from '../../../../../utils/declination';
import { manageNumberTranslationSelector } from '../selectors';
import { useAddManageNumberRedirectionMutation, useGetManageNumberMetaQuery } from '../store';
import { IManageNumberRedirectionAdd } from '../types';
import { getRedirectionAddValidation, RedirectionAddTimeRequiredOption } from '../utils';
import { ManageNumberRedirectionModalAddStyled } from './styled/ManageNumberStyled';


const ManageNumberRedirectionModalAdd = ({
                                             onCloseModal,
                                             subscriberId,
                                         }: Pick<IModalRenderProps, 'onCloseModal'> & { subscriberId: string }) => {

    const dispatch = useDispatch();

    /* Select State */

    const translationCommon = useSelector(makeSelectTranslationCommon());
    const translationManageNumber = useSelector(manageNumberTranslationSelector());

    /* Api */

    const [addRedirection, { error }] = useAddManageNumberRedirectionMutation();

    const { isLoading, isError, types, noReplayPeriod } = useGetManageNumberMetaQuery(subscriberId, {
        selectFromResult: ({ data, isLoading, isError }) => ({
            isLoading,
            isError,
            types: data?.forwarding?.types ?? [],
            noReplayPeriod: data?.forwarding?.parameters.noReplayPeriod ?? [],
        }),
    });

    useEffect(() => {
        if (isLoading || isError) return;
        dispatch(logBusinessEvent({
            eventName: ELogBusinessEventName.B2B_SHOW_REDIRECT_POPUP,
            type: ELogBusinessEventType.common,
        }))
    }, [isLoading, dispatch, isError])

    /* Handlers */

    const onSubmit = (item: IManageNumberRedirectionAdd) => {
        dispatch(logBusinessEvent({
            eventName: ELogBusinessEventName.USER_REDIRECT_CREATE_ORDER,
            type: ELogBusinessEventType.common,
        }))
        dispatch(modalSetStatusAction({ status: EModalStatus.processing }));
        addRedirection({ item, subscriberId })
            .unwrap()
            .then(() => {
                dispatch(modalCloseAll());
            })
            .catch(() => {
                dispatch(modalSetStatusAction({ status: EModalStatus.failure }));
            });
    };

    const typeOptions: IOptionItemProps[] = types.map(type => ({
        label: translationManageNumber.redirection.add.fields.type.options[type],
        value: type,
    }));

    const timeOptions: IOptionItemProps[] = noReplayPeriod.map(time => ({
        label: `${time} ${declination(+time, translationCommon.seconds)}`,
        value: time,
    }));

    return (
        <ManageNumberRedirectionModalAddStyled>
            <Modal dataName="manage-number-redirection-add" width="580px">
                <Modal.Status status={EModalStatus.start}>
                    <Modal.Title>{translationManageNumber.redirection.add.title}</Modal.Title>
                    <Formik
                        initialValues={{ type: { value: '', label: '' }, phone: '' }}
                        validationSchema={getRedirectionAddValidation(translationCommon.form.validation)}
                        onSubmit={onSubmit}
                    >
                        {({
                              dirty,
                              getFieldProps,
                              isSubmitting,
                              isValid,
                              submitForm,
                              validateField,
                          }) => (
                            <Fragment>
                                <FormFieldSelect
                                    required
                                    dataName="redirection-add-type"
                                    label={translationManageNumber.redirection.add.fields.type.label}
                                    name="type"
                                    options={typeOptions}
                                    placeholder={translationManageNumber.redirection.add.fields.type.placeholder}
                                    onSelect={() => {
                                        setTimeout(() => {
                                            validateField('time');
                                        });
                                    }}
                                />
                                <FormCondition
                                    is={RedirectionAddTimeRequiredOption}
                                    when={getFieldProps('type').value?.value}
                                >
                                    <FormFieldSelect
                                        required
                                        dataName="redirection-add-time"
                                        label={translationManageNumber.redirection.add.fields.time.label}
                                        name="time"
                                        options={timeOptions}
                                        placeholder={translationManageNumber.redirection.add.fields.time.placeholder}
                                    />
                                </FormCondition>
                                <FormFieldInputPhone
                                    required
                                    dataName="redirection-add-phone"
                                    label={translationManageNumber.redirection.add.fields.phone.label}
                                    mask={translationCommon.phoneInput.mask}
                                    name="phone"
                                />
                                <Modal.Text>
                                    {translationManageNumber.redirection.add.description}
                                </Modal.Text>
                                <Modal.Control>
                                    <ButtonServiceUserDenied
                                        disabled={!isValid || isSubmitting || !dirty}
                                        kind="medium"
                                        type="submit"
                                        onClick={submitForm}
                                    >
                                        {translationCommon.modal.apply}
                                    </ButtonServiceUserDenied>
                                    <LinkBlock onClick={onCloseModal}>
                                        {translationCommon.modal.cancel}
                                    </LinkBlock>
                                </Modal.Control>
                            </Fragment>
                        )}
                    </Formik>
                </Modal.Status>
                <Modal.Status status={EModalStatus.failure}>
                    <ModalCenterStyled>
                        <ImgStatusError/>
                        <Modal.Title>{translationCommon.modal.error}</Modal.Title>
                        <Modal.Text>
                            {
                                getErrorTranslation(
                                    error, translationCommon.error, translationManageNumber.redirection.error,
                                )
                            }
                        </Modal.Text>
                        <Modal.Control>
                            <Button onClick={onCloseModal}>{translationCommon.modal.close}</Button>
                        </Modal.Control>
                    </ModalCenterStyled>
                </Modal.Status>
            </Modal>
        </ManageNumberRedirectionModalAddStyled>
    );
};

export default ManageNumberRedirectionModalAdd;
